import { useState, useEffect, useCallback } from 'react';
import { getTipOfDay, getLoadingPhrase } from '@/lib/joy';
import { useIsLight } from '@/lib/useIsLight';
import { Lightbulb, Gem, Sparkles, Clock, TrendingUp, Flame, Trophy, Copy, Check } from 'lucide-react';
import confetti from 'canvas-confetti';
import { FacetedGem, TierGem, gemFillClip } from './GemGraphics';

// ─── Pro Tip Banner (shows during uploads/waits) ───
export function ProTipBanner() {
  const tip = getTipOfDay();
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl bg-ug-gold/5 border border-ug-gold/10" data-testid="pro-tip-banner">
      <Lightbulb className="w-4 h-4 text-ug-gold shrink-0 mt-0.5" />
      <div>
        <span className="font-sans text-[9px] font-semibold uppercase tracking-widest text-ug-gold/60 block mb-1">Pro Tip of the Day</span>
        <p className="font-sans text-xs text-zinc-300 leading-relaxed">{tip}</p>
      </div>
    </div>
  );
}

// ─── Loading Poetry (replaces boring "Loading...") ───
export function LoadingPoetry({ className = '' }) {
  const [phrase, setPhrase] = useState(getLoadingPhrase());
  useEffect(() => {
    const i = setInterval(() => setPhrase(getLoadingPhrase()), 3000);
    return () => clearInterval(i);
  }, []);
  return (
    <div className={`flex flex-col items-center justify-center gap-3 ${className}`} data-testid="loading-poetry">
      <div className="w-8 h-8 rounded-full border-2 border-ug-gold/30 border-t-ug-gold animate-spin" />
      <p className="font-sans text-xs text-zinc-500 italic animate-pulse">{phrase}</p>
    </div>
  );
}

// ─── Empty State (when no projects/data) ───
export function EmptyState({ title, subtitle, icon: Icon = Gem, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center" data-testid="empty-state">
      <div className="w-16 h-16 rounded-2xl bg-ug-gold/8 border border-ug-gold/15 flex items-center justify-center mb-5">
        <Icon className="w-7 h-7 text-ug-gold/50" strokeWidth={1.5} />
      </div>
      <h3 data-role="section-title" className="font-sans text-lg font-semibold text-zinc-200 mb-2">{title}</h3>
      <p className="font-sans text-sm text-zinc-500 max-w-sm leading-relaxed">{subtitle}</p>
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

// ─── 404 Page ───
export function NotFoundPage() {
  return (
    <div className="min-h-screen bg-ug-deepest flex items-center justify-center px-4">
      <div className="text-center">
        <div className="w-20 h-20 rounded-2xl bg-ug-gold/8 border border-ug-gold/15 flex items-center justify-center mx-auto mb-6">
          <span className="text-3xl text-ug-gold/40">404</span>
        </div>
        <h1 className="font-display text-2xl text-zinc-100 mb-3">Scene Not Found</h1>
        <p className="font-sans text-sm text-zinc-500 max-w-md mx-auto leading-relaxed mb-6">
          Looks like this scene was left on the cutting room floor. Let's get you back to the main feature.
        </p>
        <a href="/dashboard" className="inline-flex items-center gap-2 bg-ug-gold text-ug-deepest font-sans font-semibold text-sm rounded-xl py-2.5 px-6 hover:bg-ug-gold-bright transition-all shadow-ug-gold">
          Back to Dashboard
        </a>
      </div>
    </div>
  );
}

// ─── Easter Egg (click gem logo 5x for sparkle) ───
export function useEasterEgg() {
  const [clicks, setClicks] = useState(0);
  const [sparkle, setSparkle] = useState(false);

  const handleLogoClick = () => {
    const next = clicks + 1;
    setClicks(next);
    if (next >= 5) {
      setSparkle(true);
      setClicks(0);
      setTimeout(() => setSparkle(false), 2000);
    }
  };

  return { handleLogoClick, sparkle };
}

// ─── Social Caption Generator ───
export function generateCaption(project) {
  const couple = project.title || 'Wedding';
  const studio = project.studio_name || '';
  const brand = project.brand_name || studio;
  const products = (project.products || []).join(' + ');
  const eventDate = project.event_date ? new Date(project.event_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';

  const templates = [
    `${couple} ${eventDate ? `// ${eventDate}` : ''}\n\n${products ? `${products} by ${brand || 'our studio'}` : `Film by ${brand || 'our studio'}`}\n\n#weddingfilm #weddingvideographer #weddingcinema #loveintimeless`,
    `New drop: ${couple}\n\n${eventDate ? `Captured ${eventDate}` : 'A day to remember.'}\n\nEdited by the incredible team at our studio.\n\n#weddingvideo #weddingday #couplegoals #weddinginspo`,
    `${couple} — now live.\n\n${brand ? `Filmed by ${brand}` : ''}\n${products ? products : 'Highlight Film'}\n\n#weddingfilmmaker #realwedding #weddingvideography #cinematicwedding`,
  ];

  return templates[Math.floor(Math.random() * templates.length)];
}

// ─── Business Health Stats ───
export function calculateBusinessHealth(projects, userCreatedAt) {
  const completed = projects.filter(p => p.client_approved);
  const total = projects.length;
  const monthsActive = Math.max(1, Math.round((Date.now() - new Date(userCreatedAt || Date.now()).getTime()) / (30 * 24 * 60 * 60 * 1000)));
  const avgHoursPerProject = 10;
  const hoursSaved = completed.length * avgHoursPerProject;
  const shootingDaysFreed = Math.round(hoursSaved / 8);
  const projectsPerMonth = total > 0 ? (total / monthsActive).toFixed(1) : 0;
  const firstTakeApprovals = completed.filter(p => (p.current_version || 1) === 1).length;
  const approvalRate = completed.length > 0 ? Math.round((firstTakeApprovals / completed.length) * 100) : 0;
  return { totalProjects: total, completedProjects: completed.length, hoursSaved, shootingDaysFreed, projectsPerMonth, approvalRate, monthsActive };
}

// ─── Confetti Celebration ───
// In the website's colours where the client side wears its look (site-look.css, set on <html> by Layout); the
// portal's gold everywhere else.
export function fireConfetti() {
  const end = Date.now() + 1500;
  const colors = document.documentElement.classList.contains('site-look')
    ? ['#2bb3a8', '#3fd6c4', '#d94f2b', '#f4f4f5']
    : ['#c6a96f', '#d4b97e', '#5ba89d', '#f4f4f5'];
  (function frame() {
    confetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0 }, colors });
    confetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
}

// ─── Order Celebration Overlay ───
export function OrderCelebration({ show, onClose, isFirstOrder }) {
  useEffect(() => { if (show) { fireConfetti(); const t = setTimeout(onClose, 4000); return () => clearTimeout(t); } }, [show, onClose]);
  if (!show) return null;
  return (
    <div className="ug-modal fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in" onClick={onClose}>
      <div className="text-center animate-fade-in-up" onClick={e => e.stopPropagation()}>
        <div className="w-20 h-20 rounded-2xl bg-ug-gold/15 border border-ug-gold/25 flex items-center justify-center mx-auto mb-5">
          <Gem className="w-10 h-10 text-ug-gold" />
        </div>
        <h2 className="font-display text-3xl text-zinc-100 mb-2">{isFirstOrder ? 'Welcome to the Family' : 'Order Placed'}</h2>
        <p className="font-sans text-sm text-zinc-400 max-w-sm mx-auto">
          {isFirstOrder ? 'Your first order is in. Your footage is in expert hands now.' : 'Your footage is in good hands. We\'ll take it from here.'}
        </p>
      </div>
    </div>
  );
}

// ─── Gem-Bag Tier Celebration (one-shot, server-flag driven) ───
// Copy approved by operator 2026-07-17. Fires confetti on mount; both buttons
// clear the server flag via onDismiss (cross-device once — intro-modal discipline).
// The tier's own cut stone (GemGraphics.js), as the sidebar's Gem Bag card wears it; on the light card its pale
// stones take the darker tones they need there.
export function TierCelebration({ celebration, nextTier, spendToNext, onDismiss, onViewGemBag }) {
  useEffect(() => { fireConfetti(); }, []);
  const isLight = useIsLight();
  if (!celebration) return null;
  const color = celebration.badge_color || '#c6a96f';
  const isTop = !nextTier;
  return (
    <div className="ug-modal fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in" data-testid="tier-celebration-overlay">
      <div className="text-center animate-fade-in-up max-w-md mx-4 p-8 rounded-2xl bg-ug-raised border border-ug-border" onClick={e => e.stopPropagation()}>
        <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-5 border"
          style={{ backgroundColor: `${color}26`, borderColor: `${color}40` }}>
          <TierGem color={color} size={40} onLight={isLight} />
        </div>
        <h2 className="font-display text-3xl text-zinc-100 mb-2" data-testid="tier-celebration-title">
          {celebration.tier} unlocked
        </h2>
        <p className="font-sans text-sm text-zinc-300 mb-1" data-testid="tier-celebration-discount">
          Your {celebration.discount}% discount is now automatic on every order.
        </p>
        <p className="font-sans text-xs text-zinc-500 mb-6" data-testid="tier-celebration-next">
          {isTop
            ? 'Top tier. Thank you for everything you send our way.'
            : `You're $${Math.max(0, spendToNext || 0).toLocaleString()} from ${nextTier.name} (${nextTier.discount}% off).`}
        </p>
        <div className="flex items-center justify-center gap-3">
          <button onClick={onViewGemBag} data-testid="tier-celebration-view-btn"
            className="tap-auto px-5 py-2.5 rounded-md bg-gold-leaf text-obsidian font-sans text-xs font-semibold hover:bg-gold-leaf/90 transition-colors">
            View My Gem Bag
          </button>
          <button onClick={onDismiss} data-testid="tier-celebration-dismiss-btn"
            className="tap-auto px-5 py-2.5 rounded-md border border-ug-border text-slate-400 font-sans text-xs font-semibold hover:text-zinc-200 hover:bg-white/5 transition-colors">
            Keep working
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Welcome Back Banner ───
export function WelcomeBackBanner({ lastSeen, onDismiss }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!lastSeen) return;
    const diff = Date.now() - new Date(lastSeen).getTime();
    if (diff > 30 * 24 * 60 * 60 * 1000) setVisible(true); // 30+ days
  }, [lastSeen]);
  if (!visible) return null;
  return (
    <div className="mb-4 p-4 rounded-xl bg-ug-gold/5 border border-ug-gold/15 flex items-center justify-between animate-fade-in" data-testid="welcome-back-banner">
      <div className="flex items-center gap-3">
        <Sparkles className="w-5 h-5 text-ug-gold" />
        <div>
          <span className="font-sans text-sm font-semibold text-zinc-200">Welcome back!</span>
          <p className="font-sans text-xs text-zinc-500">Good to see you again. Pick up right where you left off.</p>
        </div>
      </div>
      <button onClick={() => { setVisible(false); if (onDismiss) onDismiss(); }} className="text-zinc-600 hover:text-zinc-300 text-xs font-sans">Dismiss</button>
    </div>
  );
}

// ─── Business Health One-Liner (compact, only shows after 3+ completed) ───
export function BusinessHealthBanner({ stats }) {
  if (!stats || stats.completed_projects < 3) return null;
  return (
    <div className="mb-3 px-4 py-2 rounded-xl bg-ug-raised/60 border border-ug-border flex items-center gap-4 text-xs text-zinc-400 font-sans" data-testid="business-health-banner">
      <TrendingUp className="w-3.5 h-3.5 text-ug-gold/50 shrink-0" />
      <span><strong className="text-zinc-200">{stats.total_projects}</strong> projects</span>
      <span className="text-ug-border-strong">·</span>
      <span><strong className="text-zinc-200">~{stats.hours_saved}</strong> hours saved</span>
      <span className="text-ug-border-strong">·</span>
      <span><strong className="text-zinc-200">{stats.shooting_days_freed}</strong> shooting days freed</span>
    </div>
  );
}

// ─── Social Caption Card ───
export function SocialCaptionCard({ caption }) {
  const [copied, setCopied] = useState(false);
  if (!caption) return null;
  const copy = () => { navigator.clipboard.writeText(caption); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div className="p-3 rounded-xl bg-ug-raised border border-ug-border space-y-2" data-testid="social-caption">
      <div className="flex items-center justify-between">
        <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-teal/60">Social Media Caption</span>
        <button onClick={copy} className="flex items-center gap-1 text-[9px] font-sans text-zinc-500 hover:text-ug-gold transition-colors">
          {copied ? <><Check className="w-3 h-3 text-ug-status-green" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
        </button>
      </div>
      <p className="font-sans text-xs text-zinc-300 whitespace-pre-wrap leading-relaxed">{caption}</p>
    </div>
  );
}

// ─── Editor Streak Badge ───
export function EditorStreakBadge({ onTimeStreak }) {
  if (!onTimeStreak || onTimeStreak < 3) return null;
  return (
    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-orange-500/10 border border-orange-500/20" data-testid="streak-badge">
      <Flame className="w-3 h-3 text-orange-400" />
      <span className="font-sans text-[9px] font-bold text-orange-400">{onTimeStreak} on-time streak</span>
    </div>
  );
}

// ─── Sidebar Milestone Badges (compact, always visible) ───
export function SidebarBadges({ milestones }) {
  if (!milestones || milestones.length === 0) return null;
  // A milestone whose mark is a stone wears the REAL cut gem, as the tier widget above it and the style-profile
  // progress do (GemGraphics). They were left on lucide's flat diamond when the gems were cut, so the same idea was
  // drawn two ways a few inches apart, and the badge read as the older of the two (Ryan, 2026-09-28). Everything
  // that is not a stone keeps its lucide icon, as the rest of the portal does. Same 10px box either way.
  const iconMap = { trophy: Trophy, star: Sparkles, flame: Flame, calendar: Clock, heart: Sparkles };
  return (
    <div className="flex flex-wrap gap-1 px-3 py-1.5" data-testid="sidebar-badges">
      {milestones.map(m => {
        const Icon = iconMap[m.icon];
        return (
          <div key={m.id} className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-ug-gold/8 border border-ug-gold/15" title={m.desc}>
            {Icon ? <Icon className="w-2.5 h-2.5 text-ug-gold/60" />
                  : <FacetedGem size={10} className="shrink-0" />}
            <span className="font-sans text-[7px] font-semibold text-ug-gold/70">{m.name}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Gem Progress (fills as style profile completes) ───
// A cut stone lit from the bottom up as the profile fills (Ryan: a normal gem, not the logo's): a faint grey stone
// underneath and the teal one over it, clipped to the share that is done, measured on the stone itself (GemGraphics.js,
// gemFillClip). Same 32px box as the pentagon and the gem mark before it.
export function GemProgress({ pct }) {
  const fill = Math.max(0, Math.min(100, pct));
  return (
    <div className="relative w-8 h-8 shrink-0" title={`${pct}% complete`}>
      <FacetedGem size={32} tone="grey" className="absolute inset-0" />
      <FacetedGem size={32} className="absolute inset-0 transition-all duration-700" style={{ clipPath: gemFillClip(fill, 32) }} />
    </div>
  );
}

// ─── "You Nailed It" Toast (first-take approval) ───
export function fireNailedIt() {
  confetti({ particleCount: 40, spread: 70, origin: { y: 0.6 }, colors: ['#c6a96f', '#5ba89d', '#5fa87a'] });
}
